import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Globe, Megaphone, MapPin, Clapperboard, Users, Receipt, ShieldCheck, Bot, ArrowRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getAttribution } from "@/lib/attribution";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  { icon: Globe, title: "Neue Homepage", badge: "Festpreis ab 1.990 €", text: "Modern, mobilfreundlich, mit Texten und Fotos, die Angehörige überzeugen — in 14 Tagen online, zum klaren Festpreis." },
  { icon: Megaphone, title: "Google Ads & Patientengewinnung", badge: "an jedes Budget anpassbar", text: "Deine Anzeige erscheint genau dann, wenn Angehörige in deiner Region nach Pflege suchen. Du bestimmst das Budget — wir holen das Maximum raus." },
  { icon: MapPin, title: "Google-Profil & Bewertungen", badge: "sofort umsetzbar", text: "Bei „Pflegedienst + deine Stadt“ gefunden werden: Unternehmensprofil aufpoliert, Bewertungen aufgebaut, Fotos rein." },
  { icon: Clapperboard, title: "Videos & Instagram-Anzeigen", badge: "wenn gewünscht", text: "Kurze Videos und Anzeigen für Instagram & Co. — professionell gemacht, auf deinen Pflegedienst zugeschnitten. Für Patienten und Bewerber." },
];

const MORE = [
  { icon: Users, t: "CRM-System" },
  { icon: Receipt, t: "Abrechnung SGB V/XI" },
  { icon: ShieldCheck, t: "QM für die Eröffnung" },
  { icon: Bot, t: "KI fürs Backoffice" },
];

const initial = { name: "", company: "", email: "", phone: "", service: "", message: "", consent: false };

export default function Services() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) { toast.error("Bitte fülle Name, E-Mail und Telefon aus."); return; }
    if (!form.consent) { toast.error("Bitte stimme der Datenschutzerklärung zu."); return; }
    try {
      setSubmitting(true);
      await axios.post(`${API}/leads`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        audience: "pflegedienst",
        course_interest: form.service ? `Service: ${form.service}` : "Service-Anfrage",
        message: [form.company && `Pflegedienst/Firma: ${form.company}`, form.message].filter(Boolean).join("\n"),
        consent: true,
        source: "services-joudi",
        attribution: getAttribution(),
      });
      setDone(true);
      toast.success("Danke! Wir melden uns innerhalb von 24h.");
      setForm(initial);
    } catch {
      toast.error("Das hat nicht geklappt. Bitte versuche es nochmal oder ruf uns an.");
    } finally { setSubmitting(false); }
  };

  return (
    <main id="main" className="bg-cream-100 min-h-screen" data-testid="services-page">
      {/* Hero */}
      <section className="relative pt-28 pb-16 md:pt-36 md:pb-24 overflow-hidden" style={{ backgroundColor: "#F6E8DC" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-terra" />
            <span className="font-poster text-[11px] uppercase tracking-[0.3em] font-medium text-terra">
              Services für Pflegedienste · in Partnerschaft mit Joudi Ltd.
            </span>
          </div>
          <h1 className="font-magazine font-medium text-4xl sm:text-5xl lg:text-6xl text-sand-800 leading-[1.08] max-w-3xl">
            Gute Pflege. <em className="italic text-terra">Aber keiner findet dich?</em>
          </h1>
          <p className="mt-6 text-sand-600 text-base md:text-lg leading-[1.75] max-w-2xl">
            Die meisten Pflegedienste verlieren Patienten an eine veraltete Homepage — keine Werbung, kein
            Google, keine Anfragen. Genau das lösen wir zuerst: schnell, zum Festpreis, ohne Agentur-Theater.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#anfrage" data-testid="services-hero-cta" className="inline-flex items-center gap-2 rounded-full bg-terra hover:bg-terra-dark text-white font-semibold px-6 py-3.5 transition">
              Unverbindlich anfragen <ArrowRight className="h-4 w-4" strokeWidth={1.8} />
            </a>
            <a href="tel:+4921129273330" className="inline-flex items-center rounded-full border border-sand-800/25 text-sand-800 font-semibold px-6 py-3.5 hover:border-terra hover:text-terra transition">
              0211 29273330
            </a>
          </div>
        </div>
      </section>

      {/* Services grid */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-magazine text-base md:text-lg text-terra uppercase tracking-[0.2em] font-semibold mb-10">Schnell umgesetzt — damit Anfragen kommen</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            {SERVICES.map(({ icon: Icon, title, badge, text }) => (
              <div key={title} data-testid={`service-card-${title.slice(0, 10)}`} className="bg-white/85 border border-sand-800/10 border-t-[3px] border-t-terra rounded-2xl p-7 shadow-[0_24px_50px_-36px_rgba(82,42,37,0.35)] hover:-translate-y-1 transition-transform duration-200">
                <div className="flex items-center justify-between mb-5">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-terra/10 text-terra">
                    <Icon className="h-5 w-5" strokeWidth={1.7} />
                  </span>
                  <span className="text-[11px] uppercase tracking-wider font-bold text-sage-deep bg-sage/15 rounded-full px-3 py-1">{badge}</span>
                </div>
                <h3 className="font-magazine text-xl text-sand-800">{title}</h3>
                <p className="mt-3 text-sand-600 text-sm leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap items-center gap-3">
            <span className="text-[13px] text-sand-500 font-medium">Wenn du mehr willst:</span>
            {MORE.map(({ icon: Icon, t }) => (
              <span key={t} className="inline-flex items-center gap-2 rounded-full border border-sand-800/15 bg-white/60 px-4 py-2 text-[13px] text-sand-700">
                <Icon className="h-4 w-4 text-terra" strokeWidth={1.7} /> {t}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Vorher / Nachher */}
      <section data-testid="services-before-after" className="pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-magazine text-base md:text-lg text-terra uppercase tracking-[0.2em] font-semibold mb-3">Vorher / Nachher</h2>
          <p className="text-sand-600 text-[15px] max-w-2xl mb-10">
            So sieht der Unterschied aus — ein fiktives Beispiel („Pflegedienst Mustermann“), wie wir aus einer
            veralteten Pflegedienst-Homepage einen Auftritt machen, dem Angehörige vertrauen.
          </p>
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            <figure data-testid="services-vorher">
              <div className="relative rounded-2xl overflow-hidden border border-sand-800/15 shadow-[0_24px_50px_-36px_rgba(82,42,37,0.35)]">
                <img src="/services/vorher.jpg" alt="Beispiel: veraltete Pflegedienst-Homepage" loading="lazy" className="w-full h-auto grayscale-[35%]" />
                <span className="absolute top-4 left-4 rounded-full bg-sand-800/90 text-cream-50 text-[11px] uppercase tracking-wider font-bold px-4 py-1.5">Vorher</span>
              </div>
              <figcaption className="mt-4 space-y-1.5">
                {["Wirkt wie 2008 — Angehörige klicken weg", "Am Handy kaum lesbar", "Keine Anfrage-Möglichkeit, kein Vertrauen"].map((t) => (
                  <div key={t} className="flex items-start gap-2 text-[13.5px] text-sand-600">
                    <span className="text-terra font-bold leading-[1.4]">✕</span> {t}
                  </div>
                ))}
              </figcaption>
            </figure>
            <figure data-testid="services-nachher">
              <div className="relative rounded-2xl overflow-hidden border-2 border-terra/40 shadow-[0_32px_70px_-40px_rgba(82,42,37,0.5)]">
                <img src="/services/nachher.jpg" alt="Beispiel: moderne Pflegedienst-Homepage" loading="lazy" className="w-full h-auto" />
                <span className="absolute top-4 left-4 rounded-full bg-terra text-white text-[11px] uppercase tracking-wider font-bold px-4 py-1.5">Nachher</span>
              </div>
              <figcaption className="mt-4 space-y-1.5">
                {["Warm, modern, vertrauenswürdig — ab 1.990 € Festpreis", "Perfekt am Handy, wo Angehörige suchen", "Beratungs-Button & Bewertungen direkt sichtbar"].map((t) => (
                  <div key={t} className="flex items-start gap-2 text-[13.5px] text-sand-700">
                    <Check className="h-4 w-4 text-sage-deep shrink-0 mt-0.5" strokeWidth={2.2} /> {t}
                  </div>
                ))}
              </figcaption>
            </figure>
          </div>
        </div>
      </section>

      {/* Why us */}
      <section className="py-14 md:py-20" style={{ backgroundColor: "#F6E8DC" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="font-magazine text-3xl md:text-4xl text-sand-800 leading-tight">
              Wir machen aus deinem Pflegedienst <em className="italic text-terra">eine Marke.</em>
            </h2>
            <p className="mt-5 text-sand-600 leading-[1.75]">
              Ein Auftritt, den man in deiner Stadt wiedererkennt: Logo, Farben, Fotos, Website, Anzeigen und
              Videos aus einem Guss. Dickies-Campus bildet Pflegekräfte und Leitungen weiter — unsere Partnerin{" "}
              <a href="https://joudi-group.com" target="_blank" rel="noopener noreferrer" data-testid="services-joudi-link-text" className="underline decoration-terra/40 underline-offset-2 hover:text-terra">Joudi Group</a>{" "}
              baut die Technik und die Marke dahinter, auch diese Website. Wir wissen, wonach
              Angehörige suchen und wie ein Pflegedienst Vertrauen gewinnt.
            </p>
            <ul className="mt-7 space-y-3">
              {["Erst die schnellen Hebel: Homepage, Google, Anfragen", "Dann die Marke: Wiedererkennung bei Patienten & Bewerbern", "Transparente Festpreise, klarer Zeitplan"].map((t) => (
                <li key={t} className="flex items-start gap-3 text-sand-700 text-[15px]">
                  <Check className="h-5 w-5 text-terra shrink-0 mt-0.5" strokeWidth={2} /> {t}
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-white/85 border border-sand-800/10 rounded-2xl p-8">
            <div className="text-[10px] uppercase tracking-[0.28em] font-semibold text-terra mb-3">So läuft's</div>
            <ol className="space-y-5">
              {[
                ["Kennenlernen", "Du schilderst uns, wo du stehst — Gründung, Wachstum oder Chaos im Backoffice."],
                ["Konzept & Festpreis", "Du bekommst einen klaren Vorschlag mit Umfang, Zeitplan und Preis."],
                ["Umsetzung & Übergabe", "Wir bauen, du prüfst. Danach gehört alles dir — inklusive Einweisung."],
              ].map(([t, d], i) => (
                <li key={t} className="flex gap-4">
                  <span className="font-magazine text-2xl text-terra/60 leading-none">{i + 1}</span>
                  <div>
                    <div className="font-semibold text-sand-800">{t}</div>
                    <div className="text-sm text-sand-600 mt-1">{d}</div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* Inquiry form */}
      <section id="anfrage" className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="bg-white/85 border border-sand-800/10 border-t-[3px] border-t-terra rounded-2xl p-8 md:p-10 shadow-[0_32px_70px_-40px_rgba(82,42,37,0.4)]">
            {done ? (
              <div data-testid="services-form-success" className="text-center py-10">
                <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-sage/20 text-sage mb-5">
                  <Check className="h-7 w-7" strokeWidth={2} />
                </div>
                <h3 className="font-magazine text-3xl text-sand-800">Danke, wir melden uns!</h3>
                <p className="mt-3 text-sand-600">Du erhältst innerhalb von 24 h eine Rückmeldung per E-Mail oder Telefon.</p>
                <Button onClick={() => setDone(false)} className="mt-8 rounded-full bg-terra text-white hover:bg-terra-dark">Weitere Anfrage senden</Button>
              </div>
            ) : (
              <form onSubmit={onSubmit} data-testid="services-form" className="space-y-5">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.28em] font-semibold text-terra mb-2">Anfrage</div>
                  <h2 className="font-magazine text-2xl md:text-[1.75rem] text-sand-800 leading-tight">Erzähl uns von deinem Pflegedienst</h2>
                  <p className="text-sm text-sand-600 mt-2">Unverbindlich & kostenlos. Antwort innerhalb 24 h.</p>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="sv-name" className="text-sand-700 text-xs uppercase tracking-wider font-semibold">Name *</Label>
                    <Input id="sv-name" data-testid="services-name" value={form.name} onChange={(e) => update("name", e.target.value)} className="mt-1.5 h-12 bg-cream-100 border-cream-300 rounded-xl" placeholder="Vor- & Nachname" required />
                  </div>
                  <div>
                    <Label htmlFor="sv-company" className="text-sand-700 text-xs uppercase tracking-wider font-semibold">Pflegedienst / Firma</Label>
                    <Input id="sv-company" data-testid="services-company" value={form.company} onChange={(e) => update("company", e.target.value)} className="mt-1.5 h-12 bg-cream-100 border-cream-300 rounded-xl" placeholder="Name deines Dienstes" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="sv-email" className="text-sand-700 text-xs uppercase tracking-wider font-semibold">E-Mail *</Label>
                    <Input id="sv-email" type="email" data-testid="services-email" value={form.email} onChange={(e) => update("email", e.target.value)} className="mt-1.5 h-12 bg-cream-100 border-cream-300 rounded-xl" placeholder="name@email.de" required />
                  </div>
                  <div>
                    <Label htmlFor="sv-phone" className="text-sand-700 text-xs uppercase tracking-wider font-semibold">Telefon *</Label>
                    <Input id="sv-phone" data-testid="services-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} className="mt-1.5 h-12 bg-cream-100 border-cream-300 rounded-xl" placeholder="0151 / 123 456 78" required />
                  </div>
                </div>
                <div>
                  <Label className="text-sand-700 text-xs uppercase tracking-wider font-semibold">Worum geht es?</Label>
                  <Select value={form.service} onValueChange={(v) => update("service", v)}>
                    <SelectTrigger aria-label="Worum geht es?" data-testid="services-select" className="mt-1.5 h-12 bg-cream-100 border-cream-300 rounded-xl">
                      <SelectValue placeholder="Bitte wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICES.map((s) => <SelectItem key={s.title} value={s.title}>{s.title}</SelectItem>)}
                      {MORE.map((s) => <SelectItem key={s.t} value={s.t}>{s.t}</SelectItem>)}
                      <SelectItem value="Mehreres / noch unklar">Mehreres / noch unklar</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="sv-message" className="text-sand-700 text-xs uppercase tracking-wider font-semibold">Nachricht (optional)</Label>
                  <Textarea id="sv-message" data-testid="services-message" value={form.message} onChange={(e) => update("message", e.target.value)} className="mt-1.5 bg-cream-100 border-cream-300 rounded-xl min-h-[100px]" placeholder="Wo stehst du gerade? Was brauchst du?" />
                </div>
                <div className="flex items-start gap-3 pt-1">
                  <Checkbox id="sv-consent" data-testid="services-consent" checked={form.consent} onCheckedChange={(v) => update("consent", !!v)} className="mt-0.5" />
                  <Label htmlFor="sv-consent" className="text-xs text-sand-600 leading-relaxed font-normal">
                    Ich willige ein, dass meine Angaben zur Bearbeitung meiner Anfrage verarbeitet werden. Mehr in der <Link to="/datenschutz" className="underline">Datenschutzerklärung</Link>. *
                  </Label>
                </div>
                <Button type="submit" disabled={submitting} data-testid="services-submit" className="w-full h-14 rounded-full bg-terra text-white hover:bg-terra-dark text-base font-medium group">
                  {submitting ? "Wird gesendet …" : (<>Anfrage senden <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={1.5} /></>)}
                </Button>
                <p className="text-[11px] text-sand-500 text-center">
                  Umsetzung durch unsere Technik-Partnerin <a href="https://joudi-group.com" target="_blank" rel="noopener noreferrer" data-testid="services-joudi-link" className="underline hover:text-terra">Joudi Group</a> (Peyia, Zypern) · Beratung & Ansprechpartner in Deutschland
                </p>
              </form>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}
